import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'

import ChangeInvoiceFilters from '@/views/ChangeInvoiceFilters.vue'
import ChangeOrderFilters from '@/views/ChangeOrderFilters.vue'
import ChangeOrderPaymentFilters from '@/views/ChangeOrderPaymentFilters.vue'
import DeliverySettingsView from '@/views/DeliverySettingsView.vue'
import InvoiceDetails from '@/views/InvoiceDetails.vue'
import Invoices from '@/views/Invoices.vue'
import ItemDetails from '@/views/ItemDetails.vue'
import ItemGroupDetails from '@/views/ItemGroupDetails.vue'
import ItemGroups from '@/views/ItemGroups.vue'
import Items from '@/views/Items.vue'
import Login from '@/views/Login.vue'
import More from '@/views/More.vue'
import NotFound from '@/views/NotFound.vue'
import OptionDetails from '@/views/OptionDetails.vue'
import OptionGroupDetails from '@/views/OptionGroupDetails.vue'
import OptionGroups from '@/views/OptionGroups.vue'
import Options from '@/views/Options.vue'
import OrderDetails from '@/views/OrderDetails.vue'
import OrderPayments from '@/views/OrderPayments.vue'
import Orders from '@/views/Orders.vue'
import PaymentMethodDetails from '@/views/PaymentMethodDetails.vue'
import PaymentMethods from '@/views/PaymentMethods.vue'
import Portions from '@/views/Portions.vue'
import PortionDetails from '@/views/PortionDetails.vue'
import QrCodeDetails from '@/views/QrCodeDetails.vue'
import QrCodes from '@/views/QrCodes.vue'
import Settings from '@/views/Settings.vue'

import { ifAuthenticated, ifNotAuthenticated } from './guards'
import { parseId, parseIds } from './parsers'

Vue.use(VueRouter)

const routes: RouteConfig[] = [
  {
    path: '/',
    redirect: { name: 'Orders' }
  },
  {
    path: '/about',
    name: 'About',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "about" */ '@/views/About.vue')
  },
  {
    path: '/invoices',
    name: 'Invoices',
    component: Invoices,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/invoices/filters',
    name: 'ChangeInvoiceFilters',
    component: ChangeInvoiceFilters,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/invoices/:invoiceId',
    name: 'InvoiceDetails',
    component: InvoiceDetails,
    props: parseId('invoiceId'),
    beforeEnter: ifAuthenticated
  },
  {
    path: '/menu',
    name: 'ItemGroups',
    component: ItemGroups,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/menu/:itemGroupId',
    name: 'ItemGroupDetails',
    component: ItemGroupDetails,
    props: parseId('itemGroupId'),
    beforeEnter: ifAuthenticated
  },
  {
    path: '/menu/:itemGroupId/items',
    name: 'Items',
    component: Items,
    props: parseId('itemGroupId'),
    beforeEnter: ifAuthenticated
  },
  {
    path: '/menu/:itemGroupId/items/:itemId',
    name: 'ItemDetails',
    component: ItemDetails,
    props: parseIds(['itemGroupId', 'itemId']),
    beforeEnter: ifAuthenticated
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    beforeEnter: ifNotAuthenticated
  },
  {
    path: '/more',
    name: 'More',
    component: More,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/orders',
    name: 'Orders',
    component: Orders,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/orders/filters',
    name: 'ChangeOrderFilters',
    component: ChangeOrderFilters,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/orders/:orderId',
    name: 'OrderDetails',
    component: OrderDetails,
    props: parseId('orderId'),
    beforeEnter: ifAuthenticated
  },
  {
    path: '/orderPayments',
    name: 'OrderPayments',
    component: OrderPayments,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/ordersPayments/filters',
    name: 'ChangeOrderPaymentFilters',
    component: ChangeOrderPaymentFilters,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/menu/:itemGroupId/optionGroups',
    name: 'OptionGroups',
    component: OptionGroups,
    props: parseId('itemGroupId'),
    beforeEnter: ifAuthenticated
  },
  {
    path: '/menu/:itemGroupId/optionGroups/:optionGroupId',
    name: 'OptionGroupDetails',
    component: OptionGroupDetails,
    props: parseIds(['itemGroupId', 'optionGroupId']),
    beforeEnter: ifAuthenticated
  },
  {
    path: '/menu/:itemGroupId/optionGroups/:optionGroupId/options',
    name: 'Options',
    component: Options,
    props: parseIds(['itemGroupId', 'optionGroupId']),
    beforeEnter: ifAuthenticated
  },
  {
    path: '/menu/:itemGroupId/optionGroups/:optionGroupId/options/:optionId',
    name: 'OptionDetails',
    component: OptionDetails,
    props: parseIds(['itemGroupId', 'optionGroupId', 'optionId']),
    beforeEnter: ifAuthenticated
  },
  {
    path: '/menu/:itemGroupId/items/:itemId/portions',
    name: 'Portions',
    component: Portions,
    props: parseIds(['itemGroupId', 'itemId'])
  },
  {
    path: '/menu/:itemGroupId/items/:itemId/portions/:portionId',
    name: 'PortionDetails',
    component: PortionDetails,
    props: parseIds(['itemGroupId', 'itemId', 'portionId'])
  },
  {
    path: '/paymentMethods',
    name: 'PaymentMethods',
    component: PaymentMethods,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/paymentMethods/:paymentMethodId',
    name: 'PaymentMethodDetails',
    component: PaymentMethodDetails,
    props: parseId('paymentMethodId'),
    beforeEnter: ifAuthenticated
  },
  {
    path: '/qrcodes',
    name: 'QrCodes',
    component: QrCodes,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/qrcodes/:qrCodeId',
    name: 'QrCodeDetails',
    component: QrCodeDetails,
    props: parseId('qrCodeId'),
    beforeEnter: ifAuthenticated
  },
  {
    path: '/more/delivery-settings',
    name: 'DeliverySettings',
    component: DeliverySettingsView,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/more/settings',
    name: 'Settings',
    component: Settings,
    beforeEnter: ifAuthenticated
  },
  {
    path: '*',
    name: 'NotFound',
    component: NotFound
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

export default router
